<template>
  <div class="sw">
    <ContentSkeleton v-if="loading && !tiles.length" variant="list" :rows="3" />
    <p v-else-if="error" class="dw-empty">{{ $t("dashboardWidgets.loadError") }}</p>
    <template v-else>
      <div class="sw-main">
        <span class="sw-main__value">{{ fmt(main.value) }}</span>
        <span class="sw-main__label">{{ main.label }}</span>
      </div>
      <div class="sw-tiles">
        <router-link
          v-for="t in tiles"
          :key="t.key"
          :to="link"
          class="sw-tile"
          :class="t.tone ? `sw-tile--${t.tone}` : ''"
        >
          <span class="sw-tile__value">{{ fmt(t.value) }}</span>
          <span class="sw-tile__label">{{ t.label }}</span>
        </router-link>
      </div>
    </template>
  </div>
</template>

<script>
/**
 * Indicadores chicos: salud del stock (módulo de stock) y estado de los tickets de
 * soporte. Endpoints livianos que ya existían (`/api/stock/stats`,
 * `/api/support/tickets/stats`).
 */
import { wencesApi } from "@/api/wencesApi";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";
import { formatNumberSync } from "@/utils/numberFormatter";

export default {
  name: "StatsWidget",
  components: { ContentSkeleton },
  props: {
    entry: { type: Object, required: true },
    config: { type: Object, default: () => ({}) },
  },
  data() {
    return { loading: false, error: false, data: null };
  },
  computed: {
    isSupport() {
      return this.entry.kind === "support";
    },
    link() {
      return this.isSupport ? "/support/tickets" : "/stock";
    },
    main() {
      const t = (k) => this.$t(`dashboardWidgets.stats.${k}`);
      if (!this.data) return { value: 0, label: "" };
      if (this.isSupport) {
        const c = this.data.counts || {};
        const open = (c.pending || 0) + (c.progress || 0) + (c.waiting_for_answer || 0) + (c.ready_for_test || 0);
        return { value: open, label: t("openTickets") };
      }
      return { value: (this.data.products || {}).total || 0, label: t("products") };
    },
    tiles() {
      const t = (k) => this.$t(`dashboardWidgets.stats.${k}`);
      if (!this.data) return [];
      if (this.isSupport) {
        const c = this.data.counts || {};
        return [
          { key: "pending", label: t("pending"), value: c.pending || 0, tone: "warning" },
          { key: "progress", label: t("progress"), value: c.progress || 0, tone: "primary" },
          { key: "waiting", label: t("waitingForAnswer"), value: c.waiting_for_answer || 0, tone: "danger" },
          { key: "test", label: t("readyForTest"), value: c.ready_for_test || 0, tone: "success" },
        ];
      }
      const p = this.data.products || {};
      return [
        { key: "withStock", label: t("withStock"), value: p.withStock || 0, tone: "success" },
        { key: "zero", label: t("zeroStock"), value: p.zeroStock || 0, tone: "warning" },
        { key: "negative", label: t("negativeStock"), value: p.negativeStock || 0, tone: "danger" },
      ];
    },
    menuItems() {
      return [{ key: "open", label: this.$tn("dashboardWidgets.stats.open"), icon: "external-link-alt" }];
    },
  },
  watch: {
    menuItems: {
      immediate: true,
      handler(items) {
        this.$emit("menu", items);
      },
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    fmt(n) {
      return formatNumberSync(n) || String(n || 0);
    },
    async load() {
      this.loading = true;
      this.error = false;
      try {
        const url = this.isSupport ? "/api/support/tickets/stats" : "/api/stock/stats";
        const { data } = await wencesApi.get(url);
        this.data = (data && data.data) || null;
      } catch (e) {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    onMenu(key) {
      if (key === "open") this.$router.push(this.link);
      else if (key === "refresh") this.load();
    },
  },
};
</script>

<style scoped>
.sw { display: flex; flex-direction: column; gap: 10px; }
.sw-main { display: flex; align-items: baseline; gap: 8px; }
.sw-main__value { font-size: 2rem; font-weight: 700; line-height: 1; color: var(--bs-primary); }
.sw-main__label { font-size: 0.85rem; color: var(--dw-muted); }
.sw-tiles { display: grid; grid-template-columns: repeat(auto-fit, minmax(80px, 1fr)); gap: 6px; }
.sw-tile {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 8px;
  border-radius: 8px;
  background: var(--dw-surface-2);
  border-left: 3px solid var(--dw-border);
  color: var(--dw-text);
  text-decoration: none;
}
.sw-tile:hover { color: var(--dw-text); filter: brightness(0.97); }
.sw-tile--primary { border-left-color: var(--bs-primary); }
.sw-tile--success { border-left-color: var(--bs-success); }
.sw-tile--warning { border-left-color: var(--bs-warning); }
.sw-tile--danger { border-left-color: var(--bs-danger); }
.sw-tile__value { font-weight: 700; font-size: 1.1rem; }
.sw-tile__label { font-size: 0.72rem; color: var(--dw-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
</style>
